import React, { useState, useCallback } from 'react'
import { useQueryClient } from 'react-query'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { toast } from 'sonner'
import TaskCard from './TaskCard'
import KanbanColumn from './KanbanColumn'
import { taskService } from '@/services/taskService'

const KanbanBoard = ({ projectId, board, onTaskClick, projectKey }) => {
  const queryClient = useQueryClient()
  const [activeTask, setActiveTask] = useState(null)
  const [addingLoading, setAddingLoading] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  // board = [{ id, name, category, tasks: [...] }, ...]
  const findTaskStatus = (taskId) => {
    for (const col of board) {
      if (col.tasks.find(t => t.id === taskId)) return col.id
    }
    return null
  }

  const handleDragStart = ({ active }) => {
    const taskId = parseInt(active.id.toString().replace('task-', ''))
    for (const col of board) {
      const task = col.tasks.find(t => t.id === taskId)
      if (task) { setActiveTask(task); break }
    }
  }

  const handleDragEnd = async ({ active, over }) => {
    setActiveTask(null)
    if (!over) return

    const taskId = parseInt(active.id.toString().replace('task-', ''))
    let targetStatusId = null
    let overTaskId = null

    // Dropped on a column droppable
    if (over.data?.current?.type === 'column') {
      targetStatusId = over.data.current.statusId
    }
    // Dropped on another task — use that task's column
    else if (over.data?.current?.type === 'task') {
      overTaskId = parseInt(over.id.toString().replace('task-', ''))
      targetStatusId = findTaskStatus(overTaskId)
    }
    // Dropped on column- prefix droppable
    else if (over.id?.toString().startsWith('column-')) {
      targetStatusId = parseInt(over.id.toString().replace('column-', ''))
    }

    if (!targetStatusId) return

    const currentStatusId = findTaskStatus(taskId)
    const sourceCol = board.find(c => c.id === currentStatusId)
    const targetCol = board.find(c => c.id === targetStatusId)
    if (!sourceCol || !targetCol) return

    // Save previous board state for undo
    const prevBoard = board.map(col => ({ ...col, tasks: [...col.tasks] }))

    // Same-column reorder
    if (currentStatusId === targetStatusId) {
      const tasks = [...sourceCol.tasks]
      const oldIndex = tasks.findIndex(t => t.id === taskId)
      const newIndex = overTaskId != null ? tasks.findIndex(t => t.id === overTaskId) : tasks.length - 1
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

      const reordered = arrayMove(tasks, oldIndex, newIndex)
      const updates = reordered.map((t, i) => ({ id: t.id, status_id: currentStatusId, position: i }))

      // Optimistic update
      queryClient.setQueryData(['projects', String(projectId), 'tasks', { view: 'board' }], (old) => {
        if (!old) return old
        const newBoard = old.data.board.map(col => {
          if (col.id === currentStatusId) return { ...col, tasks: reordered.map((t, i) => ({ ...t, position: i })) }
          return col
        })
        return { ...old, data: { ...old.data, board: newBoard } }
      })

      try {
        await taskService.reorderTasks(projectId, updates)
        queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      } catch {
        toast.error('Failed to reorder tasks')
        queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      }
    }
    // Cross-column move
    else {
      const sourceTasks = sourceCol.tasks.filter(t => t.id !== taskId)
      const movedTask = sourceCol.tasks.find(t => t.id === taskId)
      if (!movedTask) return

      const targetTasks = [...targetCol.tasks]
      const insertIndex = overTaskId != null
        ? targetTasks.findIndex(t => t.id === overTaskId)
        : targetTasks.length
      targetTasks.splice(insertIndex === -1 ? targetTasks.length : insertIndex, 0, { ...movedTask, status_id: targetStatusId })

      const updates = [
        ...sourceTasks.map((t, i) => ({ id: t.id, status_id: currentStatusId, position: i })),
        ...targetTasks.map((t, i) => ({ id: t.id, status_id: targetStatusId, position: i }))
      ]

      // Optimistic update
      queryClient.setQueryData(['projects', String(projectId), 'tasks', { view: 'board' }], (old) => {
        if (!old) return old
        const newBoard = old.data.board.map(col => {
          if (col.id === currentStatusId) return { ...col, tasks: sourceTasks.map((t, i) => ({ ...t, position: i })) }
          if (col.id === targetStatusId) return { ...col, tasks: targetTasks.map((t, i) => ({ ...t, position: i })) }
          return col
        })
        return { ...old, data: { ...old.data, board: newBoard } }
      })

      try {
        const targetName = targetCol.name || 'new status'
        await taskService.reorderTasks(projectId, updates)
        queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
        toast(`Task moved to ${targetName}`, {
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                // Restore previous positions
                const undoUpdates = prevBoard.flatMap(col =>
                  col.tasks.map((t, i) => ({ id: t.id, status_id: col.id, position: i }))
                )
                await taskService.reorderTasks(projectId, undoUpdates)
                queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
                toast.success('Move undone')
              } catch {
                toast.error('Failed to undo')
              }
            }
          }
        })
      } catch (err) {
        // e.g. the project's workflow rules don't allow that move
        toast.error(err.response?.data?.error || 'Failed to move task')
        queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      }
    }
  }

  const handleQuickAdd = async (statusId, title, typeId) => {
    setAddingLoading(true)
    try {
      await taskService.createTask(projectId, { title, status_id: statusId, ...(typeId ? { type_id: typeId } : {}) })
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      toast.success('Task added')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add task')
    } finally {
      setAddingLoading(false)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 min-h-[calc(100vh-12rem)]">
        {board.map(column => (
          <KanbanColumn
            key={column.id}
            status={column}
            tasks={column.tasks}
            onTaskClick={onTaskClick}
            onQuickAdd={handleQuickAdd}
            addingLoading={addingLoading}
            projectKey={projectKey}
          />
        ))}
      </div>

      {/* Drag overlay — shows the card being dragged */}
      <DragOverlay>
        {activeTask && (
          <div className="rotate-2 opacity-90">
            <TaskCard task={activeTask} isDragging projectKey={projectKey} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

export default KanbanBoard
